export const EVENTS = {
  CTA_CLICK: { name: 'cta_click' },
  NAV_CLICK: { name: 'nav_click' },
  THEME_TOGGLE: { name: 'theme_toggle' },
  SOCIAL_CLICK: { name: 'social_click' },
  FEATURE_SELECT: { name: 'feature_select' },
  SECTION_VIEW: { name: 'section_view' },
  SCROLL_DEPTH: { name: 'scroll_depth' },
  GENERATE_LEAD: { name: 'generate_lead' },
} as const;

type Payloads = {
  cta_click: {
    cta_id: string;
    cta_text?: string;
    link_url?: string;
    link_domain?: string;
    section_id?: string;
    section_title?: string;
  };
  nav_click: {
    link_url: string;
    link_domain?: string;
    link_text?: string;
    section_id?: string;
    section_title?: string;
    target_section_id?: string;
    target_section_title?: string;
  };
  feature_select: {
    feature_id: string;
    feature_title?: string;
    section_id?: string;
    section_title?: string;
  };
  section_view: {
    section_id: string;
    section_title?: string;
    section_index?: number;
    visible_ratio?: number;
  };
  scroll_depth: { depth_percent: number };
  generate_lead: { method?: string; form_id?: string; form_name?: string };
  theme_toggle: { previous_theme: 'light' | 'dark'; next_theme: 'light' | 'dark' };
  social_click: { network: 'linkedin' | 'x' | string; href: string };
};

export type EventPayloads = Payloads;

declare global {
  interface Window {
    dataLayer?: any[];
    __bs?: any;
  }
}
const DL = () => (window.dataLayer = window.dataLayer || []);

// Global deduper to survive Strict Mode remounts in dev
const W = typeof window !== 'undefined' ? (window as any) : {};
W.__bs = W.__bs || {};
W.__bs.dedupe = W.__bs.dedupe || new Map<string, number>();
const dedupe: Map<string, number> = W.__bs.dedupe;

function put(name: keyof Payloads, p: Record<string, any>) {
  // Gate: do nothing until analytics consent is granted (Basic Mode, no buffering)
  try {
    const w = window as any;
    if (!w.__analyticsGranted) return;
  } catch {}

  // auto-fill link_domain
  if ((name === 'cta_click' || name === 'nav_click') && p.link_url && !p.link_domain) {
    try {
      p.link_domain = new URL(p.link_url, location.href).hostname;
    } catch {}
  }
  // Stronger dedupe keys + TTLs to avoid dev-dup and noisy scrolls
  const key =
    name === 'scroll_depth'
      ? `scroll_depth|${p.depth_percent}`
      : name === 'section_view'
        ? `section_view|${p.section_id ?? ''}`
        : name === 'nav_click' && p.link_url
          ? `nav_click|${p.link_url}`
          : name === 'cta_click' && p.cta_id
            ? `cta_click|${p.cta_id}`
            : name === 'social_click' && p.network
              ? `social_click|${p.network}`
              : name === 'theme_toggle'
                ? `theme_toggle|${p.previous_theme}-${p.next_theme}`
                : name === 'feature_select' && p.feature_id
                  ? `feature_select|${p.feature_id}`
                  : `${name}|`;

  const ttl = name === 'scroll_depth' ? 10000 : 1000; // 10s for scroll checkpoints, 1s otherwise
  const now = Date.now();
  const last = dedupe.get(key);
  if (last && now - last < ttl) return;
  dedupe.set(key, now);

  DL().push({ event: name, ...p });
}

export const track = {
  cta: (p: Payloads['cta_click']) => put('cta_click', p),
  nav: (p: Payloads['nav_click']) => put('nav_click', p),
  theme: (p: Payloads['theme_toggle']) => put('theme_toggle', p),
  social: (p: Payloads['social_click']) => put('social_click', p),
  feature: (p: Payloads['feature_select']) => put('feature_select', p),
  sectionView: (p: Payloads['section_view']) => put('section_view', p),
  scrollDepth: (p: Payloads['scroll_depth']) => put('scroll_depth', p),
  lead: (p: Payloads['generate_lead'] = { method: 'webform' }) => put('generate_lead', p),
};

export type EventConfig = typeof EVENTS;
export type EventKey = keyof EventConfig;
export type EventName<K extends EventKey> = EventConfig[K]['name'];
export type EventParams<K extends EventKey> = Payloads[EventConfig[K]['name']];
